import { Badge, Button, ButtonText, SearchInput } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  fetchWorkspaceTags,
  IWorkspaceUserFilter,
  fetchWorkspaceUsers,
  fetchSubmissionsDashBoardConfig
} from 'src/api/queries';
import {
  Box,
  Divider,
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDeleteRight,
  faFilterList,
  faSearch,
  faUserPlus,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import SubmissionLoader from '../../Submissions/SubmissionLoader';
import AdvancedSearch from '../../Submissions/AdvancedSearch';
import {
  IAdvancedSearchCriterion,
  IUsersListItemResponse
} from '@publishing-engine/domain/models';
import SubmittedAuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import UserComposeEmailModal from 'src/components/Submission/AuthorsTab/UserComposeEmailModal';
import { AdHocModalOpen } from 'src/components/Submission/AuthorsTab/ComposeEmailModal';
import { formatCriterionValue } from 'src/utils/submissionsList';
import AddUser from 'src/components/User/AddUser/AddUser';
import { useWorkspace } from './userHook';
import UserRowItem from './UserRowItem';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

const defaultValues: IWorkspaceUserFilter = {
  journalId: [],
  role: [],
  tagId: [],
  searchParams: '',
  searchFields: []
};

type FiltersItem = 'journalId' | 'tagId' | 'role';

interface UsersPanelProps {
  showAddUser?: boolean;
  showEditUser?: boolean;
  showEditIconOnly?: boolean;
  clampAffiliation?: boolean;
  showTimestamps?: boolean;
  backButtonText?: string;
  workspaceId?: string;
  orgId?: string;
}

const UsersPanel = ({
  showAddUser = false,
  showEditUser = false,
  showEditIconOnly = false,
  clampAffiliation = false,
  showTimestamps = false,
  backButtonText = 'Back to Users',
  workspaceId: providedWorkspaceId,
  orgId: providedOrgId
}: UsersPanelProps) => {
  const [selectedUser, setSelectedUser] =
    useState<IUsersListItemResponse | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });
  const [isOpen, setIsOpen] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  const { workspaceId: fallbackWorkspaceId, orgId: fallbackOrgId } =
    useWorkspace();
  const workspaceId = providedWorkspaceId ?? fallbackWorkspaceId;
  const orgId = providedOrgId ?? fallbackOrgId;

  const { isAllowed: canAddUser } = useAuthAccessQuery({
    relation: 'canView',
    entityType: 'workspace',
    entityId: workspaceId
  });

  const { watch, resetField, reset, setValue, getValues } =
    useForm<IWorkspaceUserFilter>({
      defaultValues
    });

  const filters = watch();
  const advancedSearchCriteria = useMemo<IAdvancedSearchCriterion[]>(
    () => filters.searchFields ?? [],
    [filters.searchFields]
  );
  const selectedFilters = useMemo<Record<string, string[]>>(
    () => ({
      journalId: filters.journalId ?? [],
      tagId: filters.tagId ?? [],
      role: filters.role ?? []
    }),
    [filters.journalId, filters.tagId, filters.role]
  );
  const hasFilters =
    !!filters.journalId.length ||
    !!filters?.tagId?.length ||
    !!filters?.role?.length ||
    !!filters.searchParams ||
    !!advancedSearchCriteria.length;

  const { data: manageTags } = useQuery({
    queryKey: ['workspace-tags', { workspaceId }],
    queryFn: () => fetchWorkspaceTags(workspaceId, 'users'),
    enabled: Boolean(workspaceId)
  });

  const offset = (page - 1) * pageSize;

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['users-list', { workspaceId, filters, page, pageSize }],
    queryFn: () =>
      fetchWorkspaceUsers({
        workspaceId,
        limit: pageSize,
        offset,
        filters
      }),
    enabled: Boolean(workspaceId)
  });

  const clearFilters = () => {
    reset(defaultValues);
    setSearchValue('');
    setShowAdvancedSearch(false);
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        journalId: filters.journalId ?? [],
        tagId: filters.tagId ?? [],
        role: filters.role ?? [],
        searchParams: (filters.searchParams ?? '').trim(),
        searchFields: filters.searchFields ?? []
      }),
    [
      filters.journalId,
      filters.tagId,
      filters.role,
      filters.searchParams,
      filters.searchFields
    ]
  );

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  const totalUsers = data?.total ?? 0;

  const { data: dashBoardConfigs } = useQuery({
    queryKey: ['submission-dashboard-config', { view: 'users', workspaceId }],
    queryFn: () =>
      fetchSubmissionsDashBoardConfig({ view: 'users', workspaceId }),
    enabled: Boolean(workspaceId)
  });

  const advancedSearchFields = useMemo(
    () => dashBoardConfigs?.advancedSearchFields ?? [],
    [dashBoardConfigs?.advancedSearchFields]
  );

  // Criteria are stored as field names; label them for the active-filter chips.
  const advancedSearchLabelByField = useMemo(
    () =>
      new Map(
        advancedSearchFields.map(field => [field.value, field.label] as const)
      ),
    [advancedSearchFields]
  );

  const handleAdvancedSearchSubmit = (criteria: IAdvancedSearchCriterion[]) => {
    setValue('searchFields', criteria, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleAdvancedSearchReset = () => {
    setValue('searchFields', []);
  };

  const removeAdvancedSearchCriterion = (index: number) => {
    setValue(
      'searchFields',
      advancedSearchCriteria.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: FiltersItem
  ) => {
    const currentFilter = getValues(fieldName);
    const updatedFilter = currentFilter.filter(
      filter => filter !== filterToRemove
    );
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleFiltersSubmit = (
    nextSelectedFilters: Partial<
      Record<keyof IWorkspaceUserFilter, string | string[]>
    >
  ) => {
    (
      Object.keys(nextSelectedFilters) as (keyof IWorkspaceUserFilter)[]
    ).forEach(key => {
      const values = nextSelectedFilters[key];

      if (values === undefined) return;

      if (key === 'searchParams') {
        const value = Array.isArray(values) ? values[0] ?? '' : values;
        setValue(key, value);
        return;
      }

      const value = Array.isArray(values) ? values : [values];
      setValue(key, value);
    });
  };

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, totalUsers);

  return (
    <>
      {openSendEmailModal.status && (
        <UserComposeEmailModal
          setOpenSendEmailModal={setOpenSendEmailModal}
          organizationId={orgId}
          email={openSendEmailModal.defaultEmail}
        />
      )}
      {selectedUser?.userId ? (
        <SubmittedAuthorDetailsModal
          backButtonText={backButtonText}
          userId={selectedUser.userId}
          onBackClick={() => setSelectedUser(null)}
          roles={selectedUser.roles}
          showReviewerActivityForEachJournal
          showReviewerActivity={true}
        />
      ) : null}

      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Flex flexDirection={'column'} gap={4} width="100%">
          <Flex flexDirection={'column'} gap={2} mb={2}>
            <Flex gap={4} flexWrap="wrap">
              {showAdvancedSearch ? (
                <Box width="100%">
                  <AdvancedSearch
                    fields={advancedSearchFields}
                    value={advancedSearchCriteria}
                    onChange={handleAdvancedSearchSubmit}
                    onReset={handleAdvancedSearchReset}
                    onBack={() => setShowAdvancedSearch(false)}
                    isLoading={isFetching}
                  />
                </Box>
              ) : (
                <Flex
                  gap={2}
                  alignItems="center"
                  flex={{ base: '1 1 100%', md: '1' }}
                >
                  <SearchInput
                    placeholder="Search users by name and email"
                    iconLeft={<FontAwesomeIcon icon={faSearch} />}
                    width="100%"
                    name="searchParams"
                    value={searchValue}
                    enterKeyHint="search"
                    onChange={e => setSearchValue(e.currentTarget.value)}
                    onInputClear={() => {
                      setSearchValue('');
                      resetField('searchParams');
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setValue('searchParams', searchValue.trim());
                      }
                    }}
                    onKeyUp={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setValue('searchParams', searchValue.trim());
                      }
                    }}
                  />
                  <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                    <Button
                      variant="primary"
                      aria-label="Search"
                      onClick={() =>
                        setValue('searchParams', searchValue.trim())
                      }
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </Box>
                  {advancedSearchFields.length > 0 && (
                    <Box flexShrink={0}>
                      <Button
                        variant="secondary"
                        iconLeft={<FontAwesomeIcon icon={faFilterList} />}
                        onClick={() => setShowAdvancedSearch(true)}
                      >
                        Advanced Search
                      </Button>
                    </Box>
                  )}
                </Flex>
              )}
              {showAddUser && canAddUser && (
                <>
                  <Button
                    iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
                    onClick={() => setIsOpen(true)}
                  >
                    Add User
                  </Button>
                  {isOpen ? (
                    <AddUser
                      title="Add User"
                      submitButtonText="Add User"
                      isOpen={isOpen}
                      onClose={() => setIsOpen(false)}
                      organizationId={orgId}
                      workspaceId={workspaceId}
                    />
                  ) : null}
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex
          alignItems={'center'}
          gap={2}
          minHeight={5}
          flexWrap="wrap"
          justifyContent="space-between"
          width="100%"
        >
          <Flex alignItems={'center'} gap={2} minHeight={5} flexWrap="wrap">
            {isLoading ? (
              <Text>Searching for matches...</Text>
            ) : (
              <>
                <Text textStyle="textBody">
                  {totalUsers > 0 ? (
                    <>
                      Showing <strong>{startIndex}</strong> -{' '}
                      <strong>{endIndex}</strong> of{' '}
                      <strong>{totalUsers}</strong> user
                      {totalUsers === 1 ? '' : 's'}
                    </>
                  ) : (
                    <>
                      <strong>0</strong> users
                    </>
                  )}
                </Text>
                {hasFilters && (
                  <ButtonText
                    onClick={clearFilters}
                    leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                    variant="secondary"
                  >
                    Clear filters
                  </ButtonText>
                )}
              </>
            )}
          </Flex>

          <Divider
            orientation="horizontal"
            flex="1"
            mx={4}
            borderColor="gray.300"
            display={{ base: 'none', lg: 'block' }}
          />

          {dashBoardConfigs?.filters && (
            <Box width={{ base: '100%', sm: 'auto' }}>
              <FilterDrawer
                filterGroups={dashBoardConfigs.filters.map((filter: any) => ({
                  ...filter,
                  items: filter.items ?? []
                }))}
                watch={watch}
                onSubmit={handleFiltersSubmit}
                selectedFilters={selectedFilters}
                setSelectedFilters={handleFiltersSubmit}
              />
            </Box>
          )}
        </Flex>
        {!!hasFilters && (
          <Flex alignItems="center" gap={2} wrap="wrap">
            {dashBoardConfigs?.filters?.map(filterConfig => {
              const hasItems = (fc: any): fc is { items: any[] } =>
                Array.isArray(fc.items);

              if (!hasItems(filterConfig) || filterConfig.items.length === 0) {
                return null;
              }

              // The dashboard filter config only names the FilterDrawer's own
              // groups, so this stays a plain `string[]` read even though the
              // form now also carries the object-shaped `searchFields`.
              const filterKey = filterConfig.key as FiltersItem;
              const filterValues = filters?.[filterKey];

              if (!Array.isArray(filterValues) || filterValues.length === 0) {
                return null;
              }

              return filterValues.map(value => {
                let item = filterConfig.items.find(
                  (i: any) => i.value === value
                );
                if (!item) {
                  for (const group of filterConfig.items) {
                    if (Array.isArray(group.items)) {
                      const child = group.items.find(
                        (c: any) => c.value === value
                      );
                      if (child) {
                        item = child;
                        break;
                      }
                    }
                  }
                }

                const label = item?.name ?? value;

                return (
                  <Badge
                    key={`${filterKey}-${value}`}
                    size="base"
                    variant="interactive"
                  >
                    <Flex alignItems="center" gap={2}>
                      {label}
                      <FontAwesomeIcon
                        icon={faXmark}
                        cursor="pointer"
                        onClick={() => removeSingleFilter(value, filterKey)}
                      />
                    </Flex>
                  </Badge>
                );
              });
            })}
            {advancedSearchCriteria.map((criterion, index) => (
              <Badge
                key={`searchFields-${criterion.field}-${index}`}
                size="base"
                variant="interactive"
              >
                <Flex alignItems="center" gap={2}>
                  {advancedSearchLabelByField.get(criterion.field) ??
                    criterion.field}
                  : {formatCriterionValue(criterion.value)}
                  <FontAwesomeIcon
                    icon={faXmark}
                    cursor="pointer"
                    onClick={() => removeAdvancedSearchCriterion(index)}
                  />
                </Flex>
              </Badge>
            ))}
          </Flex>
        )}

        <Box width="100%">
          {isLoading ? (
            <SubmissionLoader
              title="Loading Search Results"
              subTitle="We are retrieving your search results. This should only take a moment."
              bgColor="neutralSecondaryFill"
            />
          ) : data?.total ? (
            <Box overflow="auto" width="100%">
              {/* @ts-ignore */}
              <Table
                size={'sm'}
                width={clampAffiliation ? '1010px' : undefined}
                minWidth="100%"
              >
                <Thead>
                  <Tr
                    gap={2}
                    data-testid="submissions-table-header"
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        background: 'white'
                      }
                    }}
                  >
                    <Th
                      p={4}
                      width={clampAffiliation ? '320px' : undefined}
                      maxWidth={clampAffiliation ? '320px' : undefined}
                    >
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Name
                      </Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem">Journals & Roles</Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem">Workspace Member</Text>
                    </Th>
                    {showTimestamps && (
                      <Th p={4}>
                        <Text textStyle="headingItem">Created Date</Text>
                      </Th>
                    )}
                    {showTimestamps && (
                      <Th p={4}>
                        <Text textStyle="headingItem">Updated Date</Text>
                      </Th>
                    )}
                    <Th p={4} aria-label="User actions" />
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.items.map(user => (
                    <UserRowItem
                      key={user.userId}
                      user={user}
                      manageTags={manageTags}
                      setOpenSendEmailModal={setOpenSendEmailModal}
                      setSelectedUser={setSelectedUser}
                      selectedUser={selectedUser}
                      orgId={orgId}
                      workspaceId={workspaceId}
                      canEditUsers={showEditUser}
                      showEditIconOnly={showEditIconOnly}
                      clampAffiliation={clampAffiliation}
                      showTimestamps={showTimestamps}
                      backButtonText={backButtonText}
                    />
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : isError ? (
            <SubmissionLoader
              title="Search Failed"
              subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
              bgColor="errorPrimaryFill"
            />
          ) : (
            <SubmissionLoader
              title="No Results Found"
              subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
              bgColor="neutralSecondaryFill"
            />
          )}
          <TablePagination
            ariaLabel="Users pagination"
            currentPage={page}
            totalItems={totalUsers}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </Box>
      </Flex>
    </>
  );
};

export default UsersPanel;
